import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useMatchOutcomePick, usePlaceMatchOutcomePick } from '@/features/games/outcomePick';
import { useWallet } from '@/features/games/rewards';
import { useAuth } from '@/lib/auth/AuthProvider';
import type {
  FixtureWithRefs,
  MatchOutcomePickRow,
  MatchOutcomeSide,
} from '@/lib/data/provider';
import { formatNumber, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { errorKey } from '@/lib/utils/errors';

/** Preset stake chips (coins). Disabled when above the user's balance. */
const STAKE_PRESETS = [50, 100, 250, 500] as const;

/** 1X2 multiplier formatted as e.g. "2.28×" (one decimal kept when round). */
function formatMultiplier(value: number, arabicNumerals: boolean): string {
  return `${localizeDigits((Math.round(value * 100) / 100).toFixed(2), arabicNumerals)}×`;
}

/** One of the three outcome buttons (HOME / DRAW / AWAY) with its multiplier. */
function OutcomeButton({
  label,
  multiplier,
  selected,
  disabled,
  onPress,
}: {
  label: string;
  multiplier: string;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      disabled={disabled}
      onPress={onPress}
      className={`min-h-[60px] flex-1 items-center justify-center gap-1 rounded-2xl border px-1.5 py-2.5 ${
        selected
          ? 'border-volt bg-volt-glass-strong'
          : 'border-line bg-inset active:bg-surface-2'
      } ${disabled && !selected ? 'opacity-50' : ''}`}
    >
      <AppText
        variant="micro"
        weight="bold"
        numberOfLines={1}
        adjustsFontSizeToFit
        className={`text-center ${selected ? 'text-volt-bright' : 'text-soft'}`}
      >
        {label}
      </AppText>
      <AppText
        variant="caption"
        weight="bold"
        tabular
        numberOfLines={1}
        className={selected ? 'text-ink' : 'text-muted'}
      >
        {multiplier}
      </AppText>
    </Pressable>
  );
}

/** Visual tone for a settled pick (won / lost / void / pending). */
function statusTone(status: MatchOutcomePickRow['status'], palette: ReturnType<typeof useThemePalette>) {
  switch (status) {
    case 'won':
      return { color: palette.win, icon: 'trophy' as const };
    case 'lost':
      return { color: palette.muted, icon: 'close-circle' as const };
    case 'void':
      return { color: palette.soft, icon: 'refresh-circle' as const };
    default:
      return { color: palette.voltBright, icon: 'hourglass' as const };
  }
}

/**
 * The locked panel shown once the user has placed a pick: which side, the stake,
 * the to-win amount, and the settled status (pending / won / lost / void).
 */
function LockedPick({
  pick,
  sideLabel,
}: {
  pick: MatchOutcomePickRow;
  sideLabel: string;
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions, arabicNumerals } = useLocale();
  const tone = statusTone(pick.status, palette);
  const toWin = Math.floor(pick.stake * pick.multiplier);

  const statusLine = (() => {
    switch (pick.status) {
      case 'won':
        return t('predictWin.statusWon', {
          payout: formatNumber(pick.payout, numeralOptions),
        });
      case 'lost':
        return t('predictWin.statusLost');
      case 'void':
        return t('predictWin.statusVoid');
      default:
        return t('predictWin.statusPending');
    }
  })();

  return (
    <View className="gap-3">
      <View
        className="flex-row items-center gap-2 rounded-2xl border px-3.5 py-3"
        style={{ borderColor: tone.color + '40', backgroundColor: tone.color + '14' }}
      >
        <Ionicons name={tone.icon} size={18} color={tone.color} />
        <View className="flex-1">
          <AppText variant="caption" weight="bold" numberOfLines={1} className="text-ink">
            {t('predictWin.yourPick', { side: sideLabel })}
          </AppText>
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {t('predictWin.stakedToWin', {
              stake: formatNumber(pick.stake, numeralOptions),
              toWin: formatNumber(toWin, numeralOptions),
            })}
          </AppText>
        </View>
        <View className="items-end">
          <AppText variant="micro" weight="bold" tabular style={{ color: tone.color }}>
            {formatMultiplier(pick.multiplier, arabicNumerals)}
          </AppText>
        </View>
      </View>
      <View className="flex-row items-center gap-1.5">
        <Ionicons name={tone.icon} size={13} color={tone.color} />
        <AppText variant="micro" weight="medium" style={{ color: tone.color }} numberOfLines={1}>
          {statusLine}
        </AppText>
      </View>
    </View>
  );
}

/**
 * "🎯 Predict & Win Coins" — a virtual-coins 1X2 game for an upcoming match.
 * Stake coins on home / draw / away at the synced multipliers; if right, the
 * stake pays out. Coins only, just for fun (no real money). Renders ONLY when
 * multipliers exist AND the match hasn't started; silent-hides otherwise.
 */
export function PredictWinCoins({ fixture }: { fixture: FixtureWithRefs }) {
  const { t } = useTranslation();
  const { locale, numeralOptions, arabicNumerals } = useLocale();
  const palette = useThemePalette();
  const toast = useToast();
  const { userId } = useAuth();

  const scheduled = fixture.status === 'scheduled';
  const pickQuery = useMatchOutcomePick(fixture.id, scheduled);
  const walletQuery = useWallet();
  const place = usePlaceMatchOutcomePick(fixture.id);

  const [side, setSide] = useState<MatchOutcomeSide | null>(null);
  const [stake, setStake] = useState<number>(STAKE_PRESETS[1]);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Auto-clear the inline error, cancelling the timer on unmount / re-error.
  useEffect(() => {
    if (!inlineError) return;
    const timer = setTimeout(() => setInlineError(null), 3000);
    return () => clearTimeout(timer);
  }, [inlineError]);

  const multipliers = pickQuery.data?.multipliers ?? null;
  const myPick = pickQuery.data?.my_pick ?? null;
  const balance = walletQuery.data?.coins ?? 0;

  const homeName = localizeTeamName(fixture.home_team, locale, true);
  const awayName = localizeTeamName(fixture.away_team, locale, true);
  const sideLabel = (s: MatchOutcomeSide) =>
    s === 'home' ? homeName : s === 'away' ? awayName : t('predictWin.draw');

  const selectedMultiplier = side && multipliers ? multipliers[side] : null;
  const potentialPayout = selectedMultiplier ? Math.floor(stake * selectedMultiplier) : 0;

  // Render only for a scheduled match that has synced multipliers OR an existing
  // pick to display. Everything else (live / finished / no prediction) hides.
  const shouldRender = scheduled && (multipliers != null || myPick != null);

  const onPlace = useMemo(
    () => () => {
      if (!userId) {
        toast.show({ message: t('predictWin.signIn'), tone: 'info' });
        return;
      }
      if (!side) {
        setInlineError(t('predictWin.choosePrompt'));
        return;
      }
      if (stake > balance) {
        setInlineError(t('errors.INSUFFICIENT_FUNDS'));
        return;
      }
      place.mutate(
        { pick: side, stake },
        {
          onSuccess: () => {
            setSide(null);
            toast.show({ message: t('predictWin.placed'), tone: 'success' });
          },
          onError: (error) => {
            const key = errorKey(error);
            setInlineError(t(key));
            toast.show({ message: t(key), tone: 'error' });
          },
        },
      );
    },
    [userId, side, stake, balance, place, toast, t],
  );

  if (!shouldRender) return null;

  return (
    <Card className="mx-4 mb-4 gap-3.5 px-4 py-4">
      {/* Header: target + bilingual title + a calm "just for fun" tag. */}
      <View className="flex-row items-center gap-2.5">
        <View
          className="h-8 w-8 items-center justify-center rounded-full"
          style={{ backgroundColor: palette.voltGlass }}
        >
          <AppText variant="caption">🎯</AppText>
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" numberOfLines={1} className="text-ink">
            {t('predictWin.title')}
          </AppText>
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {t('predictWin.subtitle')}
          </AppText>
        </View>
        <View className="rounded-full border border-line px-2 py-0.5">
          <AppText variant="micro" weight="medium" className="text-soft" numberOfLines={1}>
            {t('predictWin.forFunTag')}
          </AppText>
        </View>
      </View>

      {myPick ? (
        <LockedPick pick={myPick} sideLabel={sideLabel(myPick.pick)} />
      ) : multipliers ? (
        <>
          {/* Three outcome buttons, each with its multiplier. */}
          <View className="flex-row items-stretch gap-2">
            <OutcomeButton
              label={homeName}
              multiplier={formatMultiplier(multipliers.home, arabicNumerals)}
              selected={side === 'home'}
              disabled={place.isPending}
              onPress={() => setSide('home')}
            />
            <OutcomeButton
              label={t('predictWin.draw')}
              multiplier={formatMultiplier(multipliers.draw, arabicNumerals)}
              selected={side === 'draw'}
              disabled={place.isPending}
              onPress={() => setSide('draw')}
            />
            <OutcomeButton
              label={awayName}
              multiplier={formatMultiplier(multipliers.away, arabicNumerals)}
              selected={side === 'away'}
              disabled={place.isPending}
              onPress={() => setSide('away')}
            />
          </View>

          {/* Stake chips + the live balance. */}
          <View className="gap-2">
            <View className="flex-row items-center justify-between">
              <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
                {t('predictWin.stakeLabel')}
              </AppText>
              <View className="flex-row items-center gap-1">
                <Ionicons name="logo-bitcoin" size={12} color={palette.captain} />
                <AppText variant="micro" weight="bold" tabular style={{ color: palette.captain }}>
                  {t('predictWin.balance', { coins: formatNumber(balance, numeralOptions) })}
                </AppText>
              </View>
            </View>
            <View className="flex-row items-center gap-2">
              {STAKE_PRESETS.map((preset) => {
                const tooRich = preset > balance;
                const active = stake === preset;
                return (
                  <Pressable
                    key={preset}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active, disabled: tooRich }}
                    disabled={tooRich || place.isPending}
                    onPress={() => setStake(preset)}
                    className={`h-9 flex-1 items-center justify-center rounded-full border ${
                      active
                        ? 'border-volt bg-volt-glass'
                        : 'border-line bg-inset active:bg-surface-2'
                    } ${tooRich ? 'opacity-40' : ''}`}
                  >
                    <AppText
                      variant="micro"
                      weight="bold"
                      tabular
                      numberOfLines={1}
                      className={active ? 'text-volt-bright' : 'text-soft'}
                    >
                      {formatNumber(preset, numeralOptions)}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Live potential payout for the current side + stake. */}
          <View className="flex-row items-center justify-between rounded-2xl bg-inset px-3.5 py-2.5">
            <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
              {t('predictWin.potentialPayout')}
            </AppText>
            <View className="flex-row items-center gap-1">
              <Ionicons
                name="logo-bitcoin"
                size={13}
                color={side ? palette.voltBright : palette.muted}
              />
              <AppText
                variant="caption"
                weight="bold"
                tabular
                numberOfLines={1}
                className={side ? 'text-volt-bright' : 'text-muted'}
              >
                {side
                  ? formatNumber(potentialPayout, numeralOptions)
                  : localizeDigits('—', arabicNumerals)}
              </AppText>
            </View>
          </View>

          {/* Place CTA. */}
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ disabled: !side || place.isPending }}
            disabled={!side || place.isPending}
            onPress={onPlace}
            className={`h-11 flex-row items-center justify-center gap-2 rounded-full ${
              side && !place.isPending ? 'bg-volt active:opacity-90' : 'bg-inset'
            }`}
          >
            <Ionicons
              name="flash"
              size={15}
              color={side && !place.isPending ? palette.onVolt : palette.muted}
            />
            <AppText
              variant="caption"
              weight="bold"
              numberOfLines={1}
              className={side && !place.isPending ? 'text-on-volt' : 'text-muted'}
            >
              {place.isPending ? t('predictWin.placing') : t('predictWin.placePick')}
            </AppText>
          </Pressable>

          {inlineError ? (
            <AppText variant="micro" weight="medium" className="text-loss" numberOfLines={2}>
              {inlineError}
            </AppText>
          ) : null}
          {!userId ? (
            <AppText variant="micro" className="text-soft" numberOfLines={1}>
              {t('predictWin.signIn')}
            </AppText>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
